import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { immer } from 'zustand/middleware/immer'

export interface DrawingPoint {
  time: number
  price: number
}

export type DrawingType =
  | 'hline'
  | 'vline'
  | 'trendline'
  | 'rectangle'
  | 'text'
  | 'measure'
  | 'freehand'

export interface Drawing {
  id: string
  type: DrawingType
  /** 1 point for hline/vline/text; 2 points for trendline/rectangle/measure */
  points: DrawingPoint[]
  text?: string
  color: string
  lineWidth: number
}

interface DrawingStore {
  /** panelId → Drawing[] */
  drawings: Record<string, Drawing[]>
  addDrawing: (panelId: string, drawing: Drawing) => void
  removeDrawing: (panelId: string, id: string) => void
  updateDrawing: (
    panelId: string,
    id: string,
    patch: Partial<Pick<Drawing, 'color' | 'lineWidth' | 'text'>>
  ) => void
  clearDrawings: (panelId: string) => void
}

export const useDrawingStore = create<DrawingStore>()(
  persist(
    immer((set) => ({
      drawings: {},
      addDrawing: (panelId, drawing) =>
        set((s) => {
          if (!s.drawings[panelId]) s.drawings[panelId] = []
          s.drawings[panelId].push(drawing)
        }),
      removeDrawing: (panelId, id) =>
        set((s) => {
          const arr = s.drawings[panelId]
          if (arr) s.drawings[panelId] = arr.filter((d) => d.id !== id)
        }),
      updateDrawing: (panelId, id, patch) =>
        set((s) => {
          const arr = s.drawings[panelId]
          if (!arr) return
          const d = arr.find((x) => x.id === id)
          if (d) Object.assign(d, patch)
        }),
      clearDrawings: (panelId) =>
        set((s) => {
          s.drawings[panelId] = []
        }),
    })),
    { name: 'mhp-drawings' }
  )
)
